import { apiClient } from '@features/shared/api/client'
import type { UpdatePreferenceAssignmentsRequest, UserPreferencesResponse } from './contracts'
import type { UpdateUserPreferencesRequest, UserPreferences } from '../models'
import { mapPreferences } from '../logic/preferences'

function getPayloadUserId(payload: UpdateUserPreferencesRequest): string {
  const userId =
    payload.user_id ??
    payload.updates.find((update) => update.assignee?.type === 'user')?.assignee?.id

  if (!userId) {
    throw new Error('A user ID is required to update preferences.')
  }

  return userId
}

function toBackendPayload(
  payload: UpdateUserPreferencesRequest
): UpdatePreferenceAssignmentsRequest {
  const userId = getPayloadUserId(payload)

  return {
    updates: payload.updates.map((update) => ({
      preference_id: update.preference_id,
      assignee: {
        type: 'user',
        id: userId,
      },
      value: update.value,
    })),
  }
}

function normalizeUserPreferences(data: UserPreferencesResponse): UserPreferences {
  return {
    ...data,
    tabs: mapPreferences(data.tabs, (preference) => ({
      ...preference,
      overrides_allowed: preference.overrides_allowed && preference.user_overrides_allowed,
    })),
  }
}

export async function getUserPreferences(): Promise<UserPreferences> {
  const { data } = await apiClient.get<UserPreferencesResponse>('/users/me/preferences')
  return normalizeUserPreferences(data)
}

export async function getUserPreferencesById(userId: string): Promise<UserPreferences> {
  const { data } = await apiClient.get<UserPreferencesResponse>(`/users/${userId}/preferences`)
  return normalizeUserPreferences(data)
}

export async function updateUserPreferences(
  payload: UpdateUserPreferencesRequest
): Promise<UserPreferences> {
  const { data } = await apiClient.patch<UserPreferencesResponse>(
    '/users/me/preferences',
    toBackendPayload(payload)
  )
  return normalizeUserPreferences(data)
}

export async function updateUserPreferencesById(
  userId: string,
  payload: UpdateUserPreferencesRequest
): Promise<UserPreferences> {
  const payloadUserId = getPayloadUserId(payload)

  if (payloadUserId !== userId) {
    throw new Error('The preference update user does not match the requested user.')
  }

  const { data } = await apiClient.patch<UserPreferencesResponse>(
    `/users/${userId}/preferences`,
    toBackendPayload(payload)
  )

  return normalizeUserPreferences(data)
}
